import {getAgeniumSession} from '@/APIs/Agenium/sdk/getAgeniumSession';
import AthenaAPI from '@/APIs/AthenaAPI';
import {Outlist} from '@/Agent/Quotes/InstantAppModal';
import {getAgeniumFieldsForDeal, getApprovalFieldsForDeal} from '@/Agent/Quotes/InstantAppModalPages/utils';
import {
    getApplicationData as getAmplifyApplicationData,
    initInstantIulApplication,
} from '@/Agent/Quotes/api/instantIul';
import {createHubspotProsperDeal, updateHubspotProsperDealGetDecision} from '@/Agent/Quotes/deal';
import AgeniumSDK from '@agenium-ai/agenium-client-sdk';
import {
    ApplicationEventsEnum,
    DisplayType,
    Environment,
    QuestionType,
} from '@agenium-ai/agenium-client-sdk/dist/enum/enum';
import {
    AgeniumError,
    ApplicationData,
    Options,
    Question,
    QuestionOptions,
} from '@agenium-ai/agenium-client-sdk/dist/models';
import {ActionResult} from '@agenium-ai/agenium-client-sdk/dist/models/action';
import {Section} from '@agenium-ai/agenium-client-sdk/dist/models/section';
import _ from 'lodash';
import {useSession} from 'next-auth/react';
import React, {ReactNode, createContext, useContext, useEffect, useState} from 'react';
import {useMutation} from 'react-query';
import {EmptyError, firstValueFrom} from 'rxjs';
import constants from '../../constants';
import {useAgeniumUser} from '../hooks/useAgeniumUser';
import {useApplicationData} from '../hooks/useApplicationData';
import {useUnderwritingDecision} from '../hooks/useUnderwritingDecision';
import RootStore from '../mobx/RootStore';
import {agentDetailKeys, getAgentDetail} from './agentDetails';
import autoPatchQuestion from './autoPatchQuestions';
import {
    createDynamicBootstrap,
    createStaticBootstrap,
    messagesToSkip,
    questionsToSkip,
    skipEmptyBootstrapValue,
} from './bootstrapConstants';
import {AgeniumAgentDetail, InstantIulApplication, PatchedQuestion} from './types';

export type AgenuimContextType = {
    sdk: AgeniumSDK | undefined;
    applicationData: ApplicationData[] | undefined;
    applicationDecision: 'APPROVED' | 'REFERRED' | 'CANCELLED' | 'DECLINED' | null;
    applicationId: string;
    applicationNumber: string | undefined;
    application: InstantIulApplication | undefined;
    updateApplicationData: (input: any, formId?: string) => Promise<void>;
    currentValue: string | number | null | undefined;
    isLoading: () => boolean;
    isReady: boolean;
    isValidated: boolean;
    isRequired: boolean;
    isRepeatBlock: boolean;
    question: Question | undefined;
    initSdk: () => Promise<void>;
    addRepeatBlock: () => Promise<void>;
    getApplicationData: () => ApplicationData[];
    loadApplicationData: () => void;
    getNextSection: () => void;
    getNextQuestion: () => Promise<void>;
    getPreviousQuestion: () => Promise<void>;
    getQuestion: (id: string) => Promise<Question | undefined>;
    policyNumber: string | undefined;
    setIsValidated: React.Dispatch<React.SetStateAction<boolean>>;
    setCurrentValue: React.Dispatch<any>;
    setIsReady: React.Dispatch<React.SetStateAction<boolean>>;
    setIsRepeatBlock: React.Dispatch<React.SetStateAction<boolean>>;
    setQuestion: (question: Question | undefined) => void;
    sections: Section[] | undefined;
    showMessage: {title?: string; message: string; variant: 'error' | 'warning' | 'note' | 'text'} | null;
    submitAnswer: (question: Question) => Promise<void>;
    bootstrapData: {key: string; value: string}[];
    hasValidAgent: boolean;
    ageniumUserLoading: boolean;
};

export const AgeniumContext = createContext<AgenuimContextType | undefined>(undefined);

export default function AgeniumProvider({children, quoteResponse}: {children: ReactNode; quoteResponse: Outlist[]}) {
    const answerBySection = false;
    const {parameterStore, contactsFormStore} = RootStore.agentPortalStore;
    const {updateApplicationData} = useApplicationData();
    const [application, setApplication] = useState<any>();
    const [sessionId, setSessionId] = useState('');
    const [ageniumCaseId, setAgeniumCaseId] = useState('');
    const [internalApplicationId, setInternalApplicationId] = useState('');
    const [sdk, setSdk] = useState<AgeniumSDK>();
    const [sdkInitInProgress, setSdkInitInProgress] = useState(false);
    const [loadingCount, setLoadingCount] = useState(0);
    const [sections, setSections] = useState<Section[] | undefined>();
    const [applicationData, setApplicationData] = useState<ApplicationData[]>();
    const [amplifyApplicationData, setAmplifyApplicationData] = useState<{fetched: boolean; data: any}>({
        fetched: false,
        data: null,
    });
    const [isReady, setIsReady] = useState(true);
    const [isValidated, setIsValidated] = useState(true);
    const [isRequired, setIsRequired] = useState(true);
    let isFirstQuestionAfterResume = false;
    let lastCompletedQuestionIdFromResumeOrInit: string | undefined = undefined;
    const [question, setQuestionInternalFn] = useState<Question | undefined>();
    const setQuestion = (question: Question | undefined) => {
        setQuestionInternalFn(autoPatchQuestion(question));
    };
    const [_currentSection, setCurrentSection] = useState<Section>();
    const [nextUnansweredQuestion, setNextUnansweredQuestion] = useState(true);
    const [currentValue, setCurrentValue] = useState<string | number | null | undefined>('');
    const [isRepeatBlock, setIsRepeatBlock] = useState(false);
    const [applicationNumber, setApplicationNumber] = useState('');
    const [policyNumber, setPolicyNumber] = useState('');
    const [showMessage, setShowMessage] = useState<{
        title?: string;
        message: string;
        variant: 'error' | 'warning' | 'note' | 'text';
    } | null>(null);
    const {ageniumUser, ageniumUserPromise, loading: ageniumUserLoading} = useAgeniumUser();
    const [agentDetail, setAgentDetail] = useState<AgeniumAgentDetail | null>(null);
    const [questionIdGoingBackFrom, setQuestionIdGoingBackFrom] = useState<string | undefined>('');
    const [isGoingBack, setIsGoingBack] = useState(false);

    const session = useSession();

    const storeBulkQuestionAndAnswerMutation = useMutation({
        mutationFn: AthenaAPI.storeBulkQuestionAndAnswer,
    });

    useEffect(() => {
        if (ageniumUserPromise) {
            ageniumUserPromise.then((au) => {
                if (au === null) {
                    setAgentDetail(null);
                    return;
                }
                const detail = getAgentDetail(au);
                setAgentDetail(detail);
                if (detail) {
                    setBootstrapData((prevData) => [
                        ...prevData,
                        {key: 'Case_Agent_KeyedContractNumber', value: detail.Case_Agent_KeyedContractNumber},
                        {key: 'Case_AgentReport_AgentAssisted', value: 'Yes'}, // Always Yes when agent is present
                        {
                            key: 'Case_AgentReport_AgentAssistedContractNumber',
                            value: detail.Case_Agent_KeyedContractNumber, // Use agent's contract number
                        },
                        {key: 'Case_Agent_KeyedFirstName', value: detail.Case_Agent_KeyedFirstName},
                        {key: 'Case_Agent_KeyedLastName', value: detail.Case_Agent_KeyedLastName},
                    ]);
                }
            });
        }
    }, [ageniumUserPromise, ageniumUser]);

    useEffect(() => {
        getAmplifyApplicationData(parameterStore.formid).then((data) => {
            setAmplifyApplicationData({
                fetched: true,
                data: data || null,
            });
        });
    }, [parameterStore.formid]);

    useEffect(() => {
        if (!amplifyApplicationData.fetched) {
            return;
        }

        const data = amplifyApplicationData.data;
        if (data && data.ageniumCaseId) {
            setApplication(data);
            setAgeniumCaseId(data.ageniumCaseId);
            if (data.bootstrapData && _.isArray(data.bootstrapData) && data.bootstrapData.length > 0) {
                setBootstrapData(data.bootstrapData);
            }

            // Check if agentAssisted flag is missing and set it
            // This handles cases where an application was started on the consumer side
            // and is now being continued by an agent - we need to mark it as agent-assisted
            if (data.agentAssisted === undefined || data.agentAssisted === null) {
                console.log(
                    'Setting agentAssisted flag and contract number for application (likely consumer-to-agent transition)',
                );
                try {
                    updateApplicationData(
                        {
                            agentAssisted: true,
                            agentAssistedContractNumber: agentDetail?.Case_Agent_KeyedContractNumber || '',
                        },
                        contactsFormStore.id,
                    ).then(() => {
                        console.log('Successfully updated application with agentAssisted info');
                    });
                } catch (error) {
                    console.error('Error updating application with agentAssisted info:', error);
                }
            }
        }
    }, [amplifyApplicationData, agentDetail]);

    const staticBootstrap = createStaticBootstrap(parameterStore);
    const dynamicBootstrap = createDynamicBootstrap(quoteResponse, parameterStore);

    const [bootstrapData, setBootstrapData] = useState<{key: string; value: string}[]>(
        parameterStore.applicationId
            ? [] //[...alwaysBootstrap] // prevent Case_UWDecision_Ready from being bootstrapped
            : [...staticBootstrap, ...dynamicBootstrap].filter((item) => {
                  return skipEmptyBootstrapValue(item.key) || item.value !== '';
              }),
    );

    useEffect(() => {
        if (!ageniumCaseId) {
            setBootstrapData(
                [...staticBootstrap, ...dynamicBootstrap].filter((item) => {
                    return skipEmptyBootstrapValue(item.key) || item.value !== '';
                }),
            );
        } else {
            console.log(`Application ${ageniumCaseId} exists `);
        }
    }, [JSON.stringify(parameterStore.approval), JSON.stringify(parameterStore.prequal)]);

    const incrementLoadingCount = () => {
        setLoadingCount(loadingCount + 1);
    };

    const decrementLoadingCount = () => {
        if (loadingCount > 0) {
            setLoadingCount(loadingCount - 1);
        } else {
            setLoadingCount(0);
        }
    };
    const isLoading = () => {
        return loadingCount !== 0;
    };
    const startOrResumeApplicationOptions: Options = {
        environment: constants.NEXT_PUBLIC_AGENIUM_ENV as Environment,
        accountOptions: {version: constants.AGENIUM_VERSION as string},
    };

    const getSections = () => {
        incrementLoadingCount();
        sdk?.getSections().subscribe({
            next: (s: Section[] | undefined) => setSections(s),
            error: (err: AgeniumError) => {
                console.error(err);
                decrementLoadingCount();
            },
            complete: () => decrementLoadingCount(),
        });
    };

    const startApplication = () => {
        if (answerBySection) {
            getNextSection();
        } else {
            if (lastCompletedQuestionIdFromResumeOrInit) {
                getQuestion(lastCompletedQuestionIdFromResumeOrInit);
            } else {
                getNextQuestion();
            }
        }
    };

    const getNextSection = () => {
        console.time('getNextSection');
        incrementLoadingCount();
        sdk?.getNextSection().subscribe({
            next: (s: Section | undefined) => setCurrentSection(s),
            error: (err: AgeniumError) => {
                console.error(err);
                decrementLoadingCount();
            },
            complete: () => {
                decrementLoadingCount();
                console.timeEnd('getNextSection');
            },
        });
    };

    const getPreviousQuestion = async function () {
        const promise: Promise<Question | undefined> = new Promise((resolve, reject) => {
            incrementLoadingCount();
            if (questionIdGoingBackFrom !== '') setQuestionIdGoingBackFrom(question?.id);
            setIsGoingBack(true);
            sdk?.getPreviousQuestion({}).subscribe({
                next: async (question: Question | undefined) => {
                    resolve(question);
                },
                error: (error: any) => {
                    if (error?.message.includes('Unable to find previous question in the schema.')) reject(error);
                    else console.error(error);
                    decrementLoadingCount();
                    resolve(undefined);
                },
                complete: () => {
                    decrementLoadingCount();
                },
            });
        });

        const previousQuestion = await promise;

        if (previousQuestion) {
            if (
                previousQuestion.questionType !== QuestionType.Repeatblock &&
                previousQuestion.questionType !== QuestionType.DisplayMessage &&
                agentDetailKeys.has(previousQuestion.key)
            ) {
                console.log(`previous question was agent, going back again ${previousQuestion.key}`);
                await getPreviousQuestion();
            } else if (
                questionsToSkip.includes(previousQuestion.key) ||
                (previousQuestion.questionType === QuestionType.DisplayMessage &&
                    messagesToSkip.includes(previousQuestion.referenceId))
            ) {
                console.log(`previous question was skip question, going back again ${previousQuestion.key}`);
                await getPreviousQuestion();
            } else {
                const bootstrapValue = getBootstrapDataForKey(previousQuestion.key);
                if (bootstrapValue) {
                    console.log(`previous question was a bootstrap, going back again ${previousQuestion.key}`);
                    await getPreviousQuestion();
                } else {
                    console.log(`previous question was ok, staying here ${previousQuestion.key}`);
                    setQuestion(previousQuestion);
                    setCurrentValue(previousQuestion?.value);
                    setIsRepeatBlock(false);
                    setIsValidated(sdk && previousQuestion ? sdk?.validateQuestion(previousQuestion) : false);
                }
            }
        }
    };

    const getNextQuestion = async function (myQuestion?: Question) {
        console.time('getNextQuestion');
        const promise: Promise<Question | undefined> = new Promise((resolve) => {
            incrementLoadingCount();
            let options: Options = {};
            let questionOptions: QuestionOptions = {
                nextUnansweredQuestion: isFirstQuestionAfterResume ? false : nextUnansweredQuestion,
            };
            if (isRepeatBlock) questionOptions.forceRepeatToNextQuestion = true;
            options.questionOptions = questionOptions;
            console.log(`question options: ${JSON.stringify(options)}`);
            isFirstQuestionAfterResume = false;
            setIsRepeatBlock(false);
            // After we have gone back and then forward mark when we get back to the head
            if (myQuestion?.referenceId === questionIdGoingBackFrom) {
                setQuestionIdGoingBackFrom('');
                setIsGoingBack(false);
            }

            if (myQuestion) {
                console.log(`passed in question: ${myQuestion?.questionText}`);
                console.log('getting next question');
                sdk?.getNextQuestion(myQuestion, options).subscribe({
                    next: async (nextQuestion: Question | undefined) => {
                        console.log(`next question is ${nextQuestion?.questionText}`);
                        resolve(nextQuestion);
                    },
                    error: function (error: AgeniumError) {
                        console.error(error);
                        decrementLoadingCount();
                        const referenceIdOfFailedQuestion = error.failedTasks?.[0]?.referenceId;
                        if (referenceIdOfFailedQuestion) {
                            resolve(getQuestion(referenceIdOfFailedQuestion));
                        } else {
                            resolve(undefined);
                        }
                    },
                    complete: function () {
                        decrementLoadingCount();
                        console.log('resolving next question');
                    },
                });
            } else {
                console.log(`current question: ${question?.questionText}`);
                console.log('getting next question');
                sdk?.getNextQuestion(question, options).subscribe({
                    next: async (nextQuestion: Question | undefined) => {
                        console.log(`next question is ${nextQuestion?.questionText}`);
                        resolve(nextQuestion);
                    },
                    error: function (error: AgeniumError) {
                        console.error(error);
                        decrementLoadingCount();
                        const referenceIdOfFailedQuestion = error.failedTasks?.[0]?.referenceId;
                        if (referenceIdOfFailedQuestion) {
                            resolve(getQuestion(referenceIdOfFailedQuestion));
                        } else {
                            resolve(undefined);
                        }
                    },
                    complete: function () {
                        decrementLoadingCount();
                        console.log('resolving next question');
                    },
                });
            }
        });
        const nextQuestion = await promise.catch((e) => {
            console.error(`Error getting question ${JSON.stringify(e)}`);
        });
        if (nextQuestion) {
            console.log(`gust got ${nextQuestion.key} ${nextQuestion.questionText}`);
            if (
                nextQuestion.questionType !== QuestionType.Repeatblock &&
                nextQuestion.questionType !== QuestionType.DisplayMessage &&
                agentDetailKeys.has(nextQuestion.key)
            ) {
                nextQuestion.value = agentDetail?.[nextQuestion.key as keyof typeof agentDetail];
                await getNextQuestion(nextQuestion);
            } else if (
                questionsToSkip.includes(nextQuestion.key) ||
                (nextQuestion.questionType === QuestionType.DisplayMessage &&
                    messagesToSkip.includes(nextQuestion.referenceId))
            ) {
                console.log(`Skip display message: ${nextQuestion.referenceId} ${nextQuestion.questionText}`);
                await getNextQuestion(nextQuestion);
            } else if (nextQuestion.questionType === QuestionType.Repeatblock) {
                const bootstrapValue = getBootstrapDataForKey(nextQuestion.key);
                let currentBlockNum = parseInt(nextQuestion.value) ?? 1;
                let bootstrapBlockNum = parseInt(bootstrapValue) | 0;
                if (currentBlockNum < bootstrapBlockNum) {
                    if (sdk) {
                        const repeatBlockQuestion = await firstValueFrom(sdk.addRepeatBlockQuestionSet(nextQuestion));
                        await getNextQuestion(repeatBlockQuestion);
                    }
                } else if (
                    currentBlockNum === bootstrapBlockNum ||
                    nextQuestion.key === 'Case_BeneDetails_PrimeBlock' ||
                    nextQuestion.key === 'Case_BeneDetails_ContinBlock'
                ) {
                    await getNextQuestion(nextQuestion);
                } else {
                    setQuestion(nextQuestion);
                    setCurrentValue(nextQuestion?.value);
                }
            } else {
                const bootstrapValue = getBootstrapDataForKey(nextQuestion.key);
                const isValid = sdk?.validateQuestion(nextQuestion);

                if (
                    nextQuestion.questionType !== QuestionType.DisplayMessage &&
                    isValid &&
                    nextQuestion.value &&
                    !isGoingBack
                ) {
                    console.log(
                        `Already is valid: key ${nextQuestion.key} \n for Question: ${nextQuestion.questionText} \n has value: ${nextQuestion.value}`,
                    );
                    await getNextQuestion(nextQuestion);
                } else if (bootstrapValue || skipEmptyBootstrapValue(nextQuestion.key)) {
                    console.log(
                        `Found key ${nextQuestion.key} \n for Question: ${nextQuestion.questionText} \n has value: ${nextQuestion.value}`,
                    );
                    console.log(`existing value ${nextQuestion.value} is not valid`);
                    if (
                        nextQuestion.allowedValues &&
                        !nextQuestion?.allowedValues.find((allowedValue) => {
                            if (allowedValue.id === bootstrapValue) return true;
                        })
                    ) {
                        console.log(`Bootstrap value not found in allowed values`);
                        setQuestion(nextQuestion);
                        setCurrentValue(nextQuestion?.value);
                        console.timeEnd('getNextQuestion');
                        return;
                    }
                    console.log(`Setting value to: ${bootstrapValue}`);
                    nextQuestion.value = bootstrapValue;
                    if (sdk?.validateQuestion(nextQuestion)) {
                        await getNextQuestion(nextQuestion);
                    } else {
                        console.log(`Bootstrap value not valid for question so show question.`);
                        setQuestion(nextQuestion);
                        setCurrentValue(nextQuestion?.value);
                    }
                } else {
                    console.log(`No key found for question: ${nextQuestion.questionText}`);
                    setQuestion(nextQuestion);
                    setCurrentValue(nextQuestion?.value);
                }
            }
        }
        setNextUnansweredQuestion(false);
        console.timeEnd('getNextQuestion');
    };

    const getBootstrapDataForKey = (searchKey: string) => {
        return _.chain(bootstrapData)
            .find(function (item) {
                return item.key === searchKey;
            }) // Find the object with the matching key
            .get('value') // Get the value of the found object
            .value();
    };

    const getQuestion = async (id: string): Promise<Question | undefined> => {
        if (!id) return undefined;
        incrementLoadingCount();
        const questionObservable = sdk?.getQuestion(id);
        if (!questionObservable) return undefined;
        try {
            const question = await firstValueFrom(questionObservable);
            decrementLoadingCount();
            return question;
        } catch (error) {
            decrementLoadingCount();
            if (error instanceof EmptyError) {
                // Handle the case where the Observable completes without emitting any values
                return undefined;
            }
            throw error; // Rethrow any other errors
        }
    };

    const initApplication = async (ageniumCaseId: string) => {
        const customerId = parameterStore.formid;
        const email = parameterStore.contact;
        parameterStore.applicationId = ageniumCaseId;
        console.time('createHubspotProsperDeal');
        const instantIulApplicationId = await initInstantIulApplication(customerId, {
            email,
            ageniumCaseId,
            bootstrapData,
        });

        setInternalApplicationId(instantIulApplicationId);

        storeBulkQuestionAndAnswerMutation.mutate({
            applicationId: instantIulApplicationId,
            formId: customerId,
            questions: _.uniq(
                bootstrapData.map((data) => ({
                    questionKey: String(data.key),
                    answer: String(data.value), // Convert the answer to string
                    bootstrapped: true,
                })),
            ),
        });
        const hubspotOwnerId = await getHubspotOwnerId(
            contactsFormStore.hubspot_owner_id,
            session.data?.user?.email || undefined,
        );
        await updateApplicationData({
            hubspotId: contactsFormStore.hs_object_id,
            prosperQuoteResponse: quoteResponse,
            ...(hubspotOwnerId && {hubspotOwnerId}),
            prosperQuote: parameterStore.getQuote(),
            email: parameterStore.approval.emailAddress.valueAsString(),
            approvalFields: getApprovalFieldsForDeal(parameterStore),
            ageniumFields: getAgeniumFieldsForDeal(applicationData),
        });
        await createHubspotProsperDeal();
        console.timeEnd('createHubspotProsperDeal');
        console.time('initInstantIulApplication');
        console.timeEnd('initInstantIulApplication');
        // save new parameters applicationId and dealId
        await contactsFormStore.saveForm();
        return instantIulApplicationId;
    };

    const checkIfAppSubmitted = async (question: Question) => {
        if (question.key === 'Case_AgentReport_GI1question') {
            console.log('Updating deal with submit state');
            await updateApplicationData(
                {
                    ageniumFields: getAgeniumFieldsForDeal(applicationData),
                },
                parameterStore.formid,
            );
            await updateHubspotProsperDealGetDecision();
        }
    };

    const submitAnswer = (question: Question): Promise<void> => {
        // Ensure agent-assisted questions have correct values before submission
        // This is needed because the application may have been started on the consumer side
        // and the agent-assisted fields need to be updated when an agent takes over
        if (question.key === 'Case_AgentReport_AgentAssisted' && agentDetail) {
            question.value = 'Yes';
            console.log('Ensuring Case_AgentReport_AgentAssisted is set to Yes before submission');
        }
        if (
            question.key === 'Case_AgentReport_AgentAssistedContractNumber' &&
            agentDetail?.Case_Agent_KeyedContractNumber
        ) {
            question.value = agentDetail.Case_Agent_KeyedContractNumber;
            console.log(
                'Ensuring Case_AgentReport_AgentAssistedContractNumber is set to agent contract number before submission',
            );
        }

        const answerSubmitted: Promise<void> = new Promise(function (resolve, _reject) {
            incrementLoadingCount();
            sdk?.submitAnswer(question).subscribe({
                next: async (success: boolean) => {
                    if (success) {
                        await checkIfAppSubmitted(question);
                    } else {
                        console.error(`question ${question.questionText} was rejected ${JSON.stringify(question)}`);
                    }
                },
                error: (err: AgeniumError) => {
                    console.error('Error Submitting Question');
                    console.error(err);
                    decrementLoadingCount();
                    resolve();
                },
                complete: () => {
                    decrementLoadingCount();
                    resolve();
                },
            });
        });
        return answerSubmitted;
    };

    const addRepeatBlock = function () {
        const promise: Promise<void> = new Promise((resolve, _reject) => {
            if (question) {
                incrementLoadingCount();
                sdk?.addRepeatBlockQuestionSet(question).subscribe({
                    next: (question: Question | undefined) => {
                        setIsRepeatBlock(false);
                        setQuestion(question);
                        setCurrentValue(question?.value);
                    },
                    error: (err) => {
                        console.log(err);
                        decrementLoadingCount();
                        resolve();
                    },
                    complete: () => {
                        decrementLoadingCount();
                        resolve();
                    },
                });
            }
        });
        return promise;
    };

    const getApplicationData = function () {
        return sdk?.getApplicationData() as ApplicationData[];
    };

    const loadApplicationData = function () {
        setApplicationData(getApplicationData());
    };

    const initSdk = async () => {
        const getSession = async () => {
            if (!sessionId) {
                incrementLoadingCount();
                const ageniumSession = await getAgeniumSession(ageniumCaseId);
                if (ageniumSession.applicationId) {
                    setAgeniumCaseId(ageniumSession.applicationId);
                    parameterStore.applicationId = ageniumSession.applicationId;
                }
                setSessionId(ageniumSession.sessionId);
                decrementLoadingCount();
            }
        };
        if (!sdkInitInProgress) {
            setSdkInitInProgress(true);
            return getSession().catch(console.error);
        }
    };

    function isValidRegex(pattern: string): boolean {
        try {
            // Try to create and use the regex
            const regex = new RegExp(pattern);
            // Additional validation: try to actually use the regex
            regex.test('');

            // Check for some common invalid patterns
            if (
                pattern.includes('=') ||
                (pattern.includes('{') && !pattern.includes('}')) ||
                (pattern.includes('[') && !pattern.includes(']')) ||
                (pattern.includes('(') && !pattern.includes(')'))
            ) {
                return false;
            }

            return true;
        } catch {
            return false;
        }
    }

    useEffect(() => {
        const activeQuestion = question;
        if (activeQuestion) {
            activeQuestion.value = currentValue;
            try {
                let goFurther = true;
                if (activeQuestion.displayType === DisplayType.Error) {
                    setIsValidated(false);
                    goFurther = false;
                } else if (activeQuestion.questionType === QuestionType.Repeatblock) {
                    setIsValidated(true);
                    goFurther = false;
                } else if (activeQuestion.key === null) {
                    setIsValidated(true);
                    goFurther = false;
                } else if (activeQuestion?.validationExpressions?.length) {
                    for (const validation of activeQuestion.validationExpressions) {
                        const pattern = validation.validationExpression;
                        if (!isValidRegex(pattern) || currentValue === '' || currentValue === null) continue;
                        const testResult = RegExp(pattern, 'i').test(currentValue as string);
                        if (!testResult) {
                            setShowMessage({
                                title: 'Validation Error',
                                message: validation.validationMessage || 'N/A',
                                variant: 'error',
                            });
                            setIsValidated(false);
                            goFurther = false;
                            break;
                        } else {
                            setShowMessage(null);
                            setIsValidated(true);
                        }
                    }
                }
                if ((activeQuestion as PatchedQuestion)?.showMessageOnValue?.length) {
                    let validated = true;
                    let clearMessage = true;
                    (activeQuestion as PatchedQuestion)?.showMessageOnValue?.forEach((t) => {
                        if (t.value === currentValue) {
                            clearMessage = false;
                            setShowMessage({
                                ...(t.title ? {title: t.title} : {}),
                                message: t.message,
                                variant: t.variant ?? 'note',
                            });
                            if (t.nextDisabled) {
                                validated = false;
                                setIsValidated(false);
                            }
                        }
                    });
                    if (clearMessage) setShowMessage(null);
                    if (!validated) goFurther = false;
                }
                if (goFurther) {
                    const valid = sdk ? sdk.validateQuestion(activeQuestion) : false;
                    const isQuestionRequired =
                        activeQuestion?.validationExpressions?.some((t) => {
                            return t?.validationMessage?.toLowerCase().includes('required');
                        }) ?? false;
                    setIsValidated(
                        isQuestionRequired
                            ? !!(
                                  (typeof currentValue === 'string' && currentValue.length) ||
                                  (typeof currentValue !== 'string' &&
                                      currentValue !== null &&
                                      currentValue !== undefined)
                              ) && valid
                            : valid,
                    );
                }
            } catch (e) {
                console.error(e);
            }
        }
    }, [currentValue]);

    useEffect(() => {
        if (
            sessionId &&
            constants.NEXT_PUBLIC_AGENIUM_CLIENT_CODE &&
            constants.NEXT_PUBLIC_AGENIUM_ACCOUNT_CODE &&
            !sdk
        ) {
            setSdk(
                new AgeniumSDK(
                    {
                        accountCode: constants.NEXT_PUBLIC_AGENIUM_ACCOUNT_CODE,
                        clientCode: constants.NEXT_PUBLIC_AGENIUM_CLIENT_CODE,
                        sessionId: sessionId,
                    },
                    {
                        environment: constants.NEXT_PUBLIC_AGENIUM_ENV as Environment,
                        accountOptions: {version: constants.AGENIUM_VERSION as string},
                    },
                ),
            );
            setSdkInitInProgress(false);
        }
    }, [sessionId]);

    useEffect(() => {
        if (!amplifyApplicationData) {
            return;
        }
        async function initSdk() {
            let ageniumCaseId = amplifyApplicationData?.data?.ageniumCaseId;
            //let ageniumCaseId: any = '19087779-2c5a-4d44-b667-ecbeb85eb551';
            if (!ageniumCaseId) {
                // fall back to parameter store for backward compatibility,
                // update application data
                ageniumCaseId = parameterStore.applicationId || null;
                if (ageniumCaseId) {
                    updateApplicationData({ageniumCaseId}, parameterStore.formid);
                }
            }

            if (ageniumCaseId) {
                setAgeniumCaseId(ageniumCaseId);
            }
            if (!sdk) return;
            // wait for bootstrap data to include agent details;
            await ageniumUserPromise;

            if (ageniumCaseId) {
                incrementLoadingCount();
                sdk?.resumeApplication(ageniumCaseId, startOrResumeApplicationOptions).subscribe({
                    next: async function (result) {
                        isFirstQuestionAfterResume = true;
                        setApplicationNumber(result.applicationNumber);
                        lastCompletedQuestionIdFromResumeOrInit = result.lastCompletedQuestion;
                        setupEvents();
                        parameterStore.instantIulApplicationId = await initApplication(ageniumCaseId);
                        contactsFormStore.saveForm();
                        getSections();
                        setQuestion(undefined);
                        setCurrentSection(undefined);

                        setBootstrapData([
                            ...bootstrapData,
                            ..._.filter(getApplicationData(), (item) => {
                                return item.value !== '' && item.value !== null && item.value !== undefined;
                            }),
                        ]);
                        startApplication();
                    },
                    error: (error) => {
                        console.error(error);
                        decrementLoadingCount();
                    },
                    complete: async () => decrementLoadingCount(),
                });
            } else {
                incrementLoadingCount();
                sdk?.initialize(bootstrapData, startOrResumeApplicationOptions).subscribe({
                    next: async function (result) {
                        setupEvents();
                        setAgeniumCaseId(result.applicationId);
                        setApplicationNumber(result.applicationNumber);
                        updateApplicationData({ageniumCaseId: result.applicationId}, parameterStore.formid);
                        parameterStore.applicationId = result.applicationId;
                        parameterStore.instantIulApplicationId = await initApplication(result.applicationId);
                        contactsFormStore.saveForm();
                        setNextUnansweredQuestion(false);
                        lastCompletedQuestionIdFromResumeOrInit = result.lastCompletedQuestion;
                        getSections();
                        startApplication();
                    },
                    error: (error) => {
                        console.error(error);
                        decrementLoadingCount();
                    },
                    complete: async () => decrementLoadingCount(),
                });
            }
        }

        initSdk();

        return () => {
            sdk?.deinit();
        };
    }, [sdk, amplifyApplicationData]);

    useEffect(() => {
        const isQuestionRequired =
            question?.validationExpressions?.some((t) => {
                return t?.validationMessage?.toLowerCase().includes('required');
            }) ?? false;
        setIsRequired(isQuestionRequired);
    }, [question]);

    function updatePolicyNumber(data: ApplicationData[]) {
        setPolicyNumber(data?.find((t) => t.key === 'Case_PolicyNumber')?.value ?? '');
    }

    const setupEvents = () => {
        if (sdk) {
            sdk.applicationEvents.on(ApplicationEventsEnum.RepeatBlockComplete, () => {
                setIsRepeatBlock(true);
            });
            sdk.applicationEvents.on(ApplicationEventsEnum.ApplicationDataChange, (data: ApplicationData[]) => {
                setApplicationData(data);
                updatePolicyNumber(data);
            });
            sdk.applicationEvents.on(ApplicationEventsEnum.ApplicationCompleted, (data: ApplicationData[]) => {
                setApplicationData(data);
                updatePolicyNumber(data);
            });
            sdk.applicationEvents.on(
                ApplicationEventsEnum.QuestionActionStatus,
                (_q: Question, actionResult: ActionResult) => {
                    console.log(
                        `Question Status change: ${question?.questionText} \n ${JSON.stringify(actionResult)} `,
                    );
                    if (actionResult.applicationData) {
                        if (isFirstQuestionAfterResume) {
                            setBootstrapData(
                                _.filter(actionResult.applicationData, (item) => {
                                    return item.value !== '' && item.value !== null && item.value !== undefined;
                                }),
                            );
                        }
                        setApplicationData(actionResult.applicationData);
                        updatePolicyNumber(actionResult.applicationData);
                    }
                },
            );
            sdk.applicationEvents.on(
                ApplicationEventsEnum.SectionActionStatus,
                (_q: Question, actionResult: ActionResult) => {
                    if (actionResult.applicationData) {
                        setApplicationData(actionResult.applicationData);
                        updatePolicyNumber(actionResult.applicationData);
                    }
                },
            );
        }
    };

    // Derived State
    const applicationDecision = useUnderwritingDecision(
        applicationData || null,
        internalApplicationId,
        parameterStore.formid,
    );

    return (
        <AgeniumContext.Provider
            value={{
                sdk,
                applicationData,
                applicationDecision,
                applicationId: ageniumCaseId,
                applicationNumber,
                application,
                updateApplicationData,
                currentValue,
                isLoading,
                isReady,
                isValidated,
                isRequired,
                isRepeatBlock,
                question,
                initSdk,
                addRepeatBlock,
                getApplicationData,
                loadApplicationData,
                getNextSection,
                getNextQuestion,
                getPreviousQuestion,
                getQuestion,
                policyNumber,
                setIsValidated,
                setCurrentValue,
                setIsReady,
                setIsRepeatBlock,
                setQuestion,
                sections,
                showMessage,
                submitAnswer,
                bootstrapData,
                hasValidAgent: !!agentDetail,
                ageniumUserLoading,
            }}
        >
            {children}
        </AgeniumContext.Provider>
    );
}

export function useAgenium(): AgenuimContextType {
    const context = useContext(AgeniumContext);
    if (!context) throw new Error('useAgenium must be used within a AgeniumProvider');
    return context;
}

async function getHubspotOwnerId(hubspotOwnerId: string, agentEmail?: string) {
    let finalHubspotOwnerId = hubspotOwnerId;
    if (agentEmail) {
        try {
            const {agentData} = await AthenaAPI.getInternalUser(agentEmail);
            finalHubspotOwnerId = agentData?.hubspotId || hubspotOwnerId.toString();
        } catch (error) {
            console.error('Error fetching agent data:', error);
        }
    }
    return finalHubspotOwnerId;
}
